import { Dialog, Box, Typography, Button } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import { EmojiEvents } from '@mui/icons-material'
import type { Achievement } from '@/types/achievements'
import { TIER_GRADIENTS } from '@/types/achievements'
import Confetti from 'react-confetti'
import { useEffect, useState } from 'react'

interface AchievementUnlockProps {
  achievement: Achievement | null
  open: boolean
  onClose: () => void
}

export default function AchievementUnlock({ achievement, open, onClose }: AchievementUnlockProps) {
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight })
  const [showConfetti, setShowConfetti] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (open) {
      setShowConfetti(true)
      const timer = setTimeout(() => setShowConfetti(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [open])

  if (!achievement) return null

  return (
    <>
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
        />
      )}

      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'visible',
          },
        }}
      >
        <AnimatePresence>
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: 'spring', duration: 0.5 }}
          >
            <Box
              sx={{
                background: TIER_GRADIENTS[achievement.tier],
                color: 'white',
                p: 4,
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Animated background elements */}
              <motion.div
                animate={{
                  rotate: 360,
                }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                style={{
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  opacity: 0.1,
                }}
              >
                <EmojiEvents sx={{ fontSize: 200 }} />
              </motion.div>

              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  Achievement Unlocked!
                </Typography>
              </motion.div>

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: [0, 360] }}
                transition={{ delay: 0.2, type: 'spring', duration: 0.8 }}
              >
                <Box
                  sx={{
                    fontSize: 96,
                    my: 2,
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                  }}
                >
                  {achievement.icon}
                </Box>
              </motion.div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  {achievement.display_name}
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    opacity: 0.95,
                    textShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  {achievement.description}
                </Typography>

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 28 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    +{achievement.points} Points
                  </Typography>
                </Box>
              </motion.div>
            </Box>

            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={onClose}
                sx={{
                  background: TIER_GRADIENTS[achievement.tier],
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 700,
                }}
              >
                Awesome!
              </Button>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Dialog>
    </>
  )
}
